import React, { useState, useEffect, useLayoutEffect, useCallback, useRef, useMemo, useTransition } from 'react';
import { clickEndsPress, isPlainMousePress } from '../hooks/usePressAction'; // [FLUID-8 2026-10-12]
import styled from 'styled-components';
import { lgButtonPrimary, lgSunken, lgTile, LG_PRESS, LgPressProps } from '../styles/liquidGlass'; // [LIQUID-GLASS 2026-11-02]
import {
  fetchSalesScoreboard, updateScoreboardLevers, peekCache,
  SalesScoreboardData, ScoreboardRow, ScoreboardTier, ScoreboardPeriod,
} from '../services/api';
import { SlideViewport, SlidePane, slidePos } from '../components/ViewSlide'; // [VIEW-SLIDE 2026-10-08]
import { useRowsLand, useLandWindow, fluidCascade } from '../components/Fluid'; // [FLUID-6 2026-10-12]
import { effectiveCurrentMonth, addMonthsToYYYYMM, MIN_DROPDOWN_MONTH } from '../utils/effectiveMonth';
import { SkeletonBox } from '../components/Skeleton';
import { LoadFailed, isForbidden } from '../components/LoadState'; // [LOAD-STATES 2026-09-29]
import ScaleToFit from '../components/ScaleToFit';
import GlassSelect from '../components/GlassSelect';
import { toastSuccess, toastError } from '../components/toastService';
import { SaveButtonContent, useSavePhase } from '../components/SaveIndicator'; // [MOTION-B 2026-08-20]
import { useAuth } from '../contexts/AuthContext';
import useIsMobile from '../hooks/useIsMobile'; // [SCOREBOARD PHONE KPI 2026-09-23, user]
import { pillTrack, pillSegment } from '../styles/buttons'; // [BUTTON-STYLE 2026-09-30]


/* [PERF 2026-08-14] Locale formatters built ONCE — `toLocale*(locale, {options})`
 * constructs a fresh Intl formatter per call (measured 34us money / 90us date on V8,
 * vs 0.7us / 1.5us cached). Calls WITHOUT options are already fast and left alone. */
const MONEY_FMT = new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/**
 * Sales Scoreboard — roster-wide sales leaderboard over the last 6 completed
 * months (from Leads_Raw + the HubSpot roster/role). Frozen top band (roster KPI
 * strip + tier-lever bar) over a scrolling ranked table; Elite rows are tinted
 * green like the source sheet. Tier bars are admin-editable and re-tier live.
 */

/* ── helpers ── */
// [NET-IN-BANK 2026-09-16] Net In-Bank (and ATV / Monthly off it) can go negative when a rep's
// refunds outweigh their sales — sign before the $: "-$200.00", not "$-200.00".
const fmtCurrency = (n: number) => {
  const v = Number(n) || 0;
  return `${Math.round(v * 100) < 0 ? '-' : ''}$${MONEY_FMT.format(Math.abs(v))}`;
};
const fmtCurrency0 = (n: number) => {
  const v = Math.round(Number(n) || 0);
  return `${v < 0 ? '-' : ''}$${Math.abs(v).toLocaleString('en-US')}`;
};
const fmtInt = (n: number) => (Number(n) || 0).toLocaleString('en-US');
const fmtPct = (n: number) => `${(Number(n) || 0).toFixed(2)}%`;
// [PERF 2026-08-14] Built once — `toLocaleDateString(locale, options)` constructs a fresh
// Intl.DateTimeFormat on every call. Cheap here (a handful of labels, not thousands of cells),
// but it is the same trap that cost Inventory 84ms a render, so it is not worth reintroducing.
const MONTH_ABBR_FMT = new Intl.DateTimeFormat('en-US', { month: 'short' });
const MONTH_FULL_FMT = new Intl.DateTimeFormat('en-US', { month: 'long', year: 'numeric' });

/** 'Feb-2026' — the one month format on this page (pill label + snapshot meta). */
const fmtMonthYYYY = (m: string) => {
  const [y, mo] = m.split('-');
  // [DATE-STYLE 2026-09-29, user] "Mar-2026" → "Mar 2026" (one date style across the app).
  return `${MONTH_ABBR_FMT.format(new Date(Number(y), Number(mo) - 1))} ${y}`;
};
/** 'August 2026' — the right-hand pill label. */
const fmtMonthFull = (m: string) => {
  const [y, mo] = m.split('-');
  return MONTH_FULL_FMT.format(new Date(Number(y), Number(mo) - 1));
};

/* ── layout (sticky-freeze scaffold like My Sales / Refunds) ── */
const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  min-width: 0;
  overflow: hidden;
  background: ${(p) => p.theme.surface};

  @media (max-width: 768px) {
    overflow-y: auto;
    /* [PHONE-LAYOUT 2026-10-23, user] ...and it IS the one scroll of the page now (ScrollContainer below no longer
       scrolls up / down on phones): "nothing will be fixed on the top side". */
    overscroll-behavior-y: contain;
    -webkit-overflow-scrolling: touch;
  }
`;

const FrozenTop = styled.div`
  flex-shrink: 0;
  padding: 14px 16px 0;
  background: ${(p) => p.theme.surface};
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 12px;

  /* [SCOREBOARD PHONE HEADER 2026-09-23, user] Phones: the title and the period pill do not fit
     on one line, so the pill used to wrap under a left-aligned title. Stack them and centre both:
     title in the middle, pill centred under it. The knob is measured from the active option's
     offset INSIDE the pill, so centring the pill does not move it. Desktop unchanged. */
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: center;
    gap: 8px;
  }
`;

const PageTitle = styled.h1`
  font-size: 16px;
  font-weight: 700;
  color: ${(p) => p.theme.text};
  margin: 0;
  white-space: nowrap;
`;

/* ── [PERIOD-PILL 2026-08-14] window switch ──────────────────────────────────
   Same sliding pill as My Sales' "Leads to Sales / Overall Sales" toggle, at the user's
   request. ONE difference, and it is why the knob is measured rather than a flat 50%:
   My Sales' two labels are near enough the same length to share equal halves, whereas
   "Last 6 completed months · Feb-26 – Jul-26" is roughly triple "August 2026". Forcing
   equal halves (flex: 1 1 0) would make the pill about twice as wide as it needs to be and
   leave the short side swimming in space, so each option sizes to its own content and the
   knob is positioned from the active button's real offset/width. */
/* [BUTTON-STYLE 2026-09-30] Track and option sizes come from styles/buttons (pillTrack / pillSegment),
   the one switch-pill size; the knob insets follow the 3px track padding (its left/width stay measured). */
const Pill = styled.div`
  position: relative;
  display: inline-flex;
  align-items: stretch;
  ${pillTrack}
  background: ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(22,25,23,0.05)')};
  border: 1px solid ${(p) => p.theme.line};
  /* [LIQUID-GLASS 2026-11-02, user] */
  background: ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(0,0,0,0.30)' : 'rgba(255,255,255,0.66)')};
  border-color: transparent;
  box-shadow: ${(p) => (p.theme.colorScheme === 'dark' ? 'inset 0 1px 2px rgba(0,0,0,0.5), inset 0 -1px 0 rgba(255,255,255,0.07)' : 'inset 0 1px 2px rgba(22,25,23,0.14), inset 0 0 0 1px rgba(22,25,23,0.08)')};
`;
/* The sliding brand-green highlight. `transform` (not `left`) so the slide is composited. */
const PillKnob = styled.div<{ $left: number; $width: number }>`
  position: absolute;
  top: 3px;
  bottom: 3px;
  left: 0;
  width: ${(p) => p.$width}px;
  transform: translateX(${(p) => p.$left}px);
  border-radius: 999px;
  background: ${(p) => (p.theme.colorScheme === 'dark' ? '#4acd8b' : p.theme.green600)};
  transition: transform 240ms cubic-bezier(0.4, 0, 0.2, 1), width 240ms cubic-bezier(0.4, 0, 0.2, 1);
  /* Until the first measurement lands there is no width, so nothing is painted — that avoids a
     knob flashing at the wrong size on mount. */
  opacity: ${(p) => (p.$width ? 1 : 0)};
  /* [LIQUID-GLASS 2026-11-02, user] */
  background: linear-gradient(180deg, rgba(255,255,255,0.32), rgba(255,255,255,0) 55%), ${(p) => (p.theme.colorScheme === 'dark' ? '#4acd8b' : p.theme.green600)};
  box-shadow: 0 2px 8px ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(74,205,139,0.30)' : 'rgba(20,107,84,0.30)')}, inset 0 1px 0 rgba(255,255,255,0.45), inset 0 -1px 0 rgba(0,0,0,0.12);
`;
const PillOpt = styled.button<{ $active: boolean }>`
  position: relative;
  z-index: 1;
  flex: 0 0 auto;
  border: none;
  background: transparent;
  cursor: pointer;
  ${pillSegment}
  color: ${(p) => (p.$active
    ? (p.theme.colorScheme === 'dark' ? '#06281d' : '#ffffff')
    : p.theme.muted)};
  transition: color 180ms cubic-bezier(0.4, 0, 0.2, 1);

  /* [BUTTON-STYLE 2026-09-30] At the full pill size the two long labels come to about 331px, wider
     than a 360px phone leaves (about 310px). Same phone trim as the Stats pill (StatsPage Seg). */
  @media (max-width: 768px) {
    padding: 7px 12px;
    font-size: 12px;
  }
`;

/* ── roster KPI strip ── */
const KPI_CELL_W = 150;
const KPI_GAP = 10;
const kpiRowMinWidth = (n: number) => n * KPI_CELL_W + (n - 1) * KPI_GAP;

const Snapshot = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 12px 14px;
  border-radius: 14px;
  margin-bottom: 12px;
  background: ${(p) =>
    p.theme.colorScheme === 'dark'
      ? 'linear-gradient(180deg, rgba(255,255,255,0.045) 0%, rgba(255,255,255,0.02) 100%)'
      : 'linear-gradient(180deg, #f5f8fc 0%, #eceeed 100%)'};
  border: 1px solid ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(255,255,255,0.10)' : 'rgba(22,25,23,0.09)')};
`;

const SnapHead = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 10px;

  /* [PHONE-LAYOUT 2026-10-23, user] Phones, Monthly Scoreboard: "ROSTER TOTAL" and "24 reps · Oct 2026" were squeezed
     into two lines each beside the month box. Now the title and the meta share the first line (whole, never split)
     and the month box takes the second line, full width (SnapRight lets both join this row on phones). */
  @media (max-width: 768px) {
    flex-wrap: wrap;
    align-items: center;
    row-gap: 8px;
  }
`;

const SnapTitle = styled.span`
  font-size: 11px;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  color: ${(p) => (p.theme.colorScheme === 'dark' ? '#4acd8b' : p.theme.green600)};

  @media (max-width: 768px) {
    white-space: nowrap; /* [PHONE-LAYOUT 2026-10-23] one line */
  }
`;

const SnapMeta = styled.span`
  font-size: 12px;
  font-weight: 600;
  color: ${(p) => p.theme.muted};

  @media (max-width: 768px) {
    white-space: nowrap; /* [PHONE-LAYOUT 2026-10-23] one line, at the right of the title */
    margin-left: auto;
  }
`;

/* ── [MONTH-PICKER 2026-09-02, user] the month dropdown ───────────────────────
   It sits on the RIGHT of "3 reps · Aug-2026", inside the Roster Total box, at the user's
   request. SnapHead aligns on the BASELINE, which is what we want between "ROSTER TOTAL" and
   the meta text — so the select is wrapped rather than dropped in directly: the wrapper
   contributes the meta's baseline to SnapHead, and centres the taller select against that
   text itself. Nothing above it moves. */
const SnapRight = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;

  /* [PHONE-LAYOUT 2026-10-23] Phones: the meta and the month box join SnapHead's wrapping row (see SnapHead). */
  @media (max-width: 768px) {
    display: contents;
  }
`;

/* Sized down from GlassSelect's default trigger to sit beside 12px meta text without
   inflating the row. `&&` for specificity, the same way AddPaymentModal narrows it. */
const MonthSelect = styled(GlassSelect)`
  && {
    height: 26px;
    padding: 0 26px 0 10px;
    border-radius: 8px;
    font-size: 11.5px;
    font-weight: 700;
    letter-spacing: 0.2px;
  }
  /* [PHONE-LAYOUT 2026-10-22] Phones: a 34 px tall finger target (26 px was too small to tap reliably).
     [PHONE-LAYOUT 2026-10-23] ...now the full second line of the Roster Total head, 36 px, like the app's other
     phone dropdowns. */
  @media (max-width: 768px) {
    && { height: 36px; font-size: 13px; flex: 1 0 100%; width: 100%; }
  }
`;

const KpiRow = styled.div<{ $land?: boolean }>`
  display: flex;
  gap: ${KPI_GAP}px;

  @media (max-width: 768px) {
    overflow-x: auto;
    scrollbar-width: none;
    &::-webkit-scrollbar { display: none; }
  }

  /* [FLUID-6 2026-10-12, user] "sales scoreboard doesn't have the animation when someone comes to that tab": when the
     figures land after their skeleton (a tab visit, a month pick, a side of the pill that was not cached) the tiles
     rise in one after another, 18 ms apart as on a pill switch (components/Fluid.tsx). Only while $land
     (useLandWindow), so a tier-bar save or the quiet re-check after a cached switch never replays it. */
  ${(p) => (p.$land ? fluidCascade('> [data-slide-item]', 0, 18, 7) /* [FLUID-8 2026-10-12] soft: readable at once */ : '')}
`;

const Kpi = styled.div`
  flex: 1 1 0;
  min-width: 0;
  padding: 9px 13px;
  border-radius: 10px;
  background: ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(255,255,255,0.035)' : '#ffffff')};
  border: 1px solid ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(22,25,23,0.08)')};
  box-shadow: ${(p) => (p.theme.colorScheme === 'dark' ? 'none' : '0 1px 2px rgba(22,25,23,0.05)')};

  @media (max-width: 768px) {
    flex: 0 0 calc((100% - 10px) / 2 - 14px);
  }
  /* [LIQUID-GLASS 2026-11-02, user] */
  ${lgTile}
`;

const KpiLabel = styled.div`
  font-size: 9.5px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  color: ${(p) => p.theme.muted};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  /* [PHONE-LAYOUT 2026-10-22] Phones: 11 px and the label wraps instead of ending in "..." ("AVG, MONTHLY CONV %"
     was cut in the 2-up swipe tiles). */
  @media (max-width: 768px) {
    font-size: 11px;
    white-space: normal;
    overflow: visible;
    line-height: 1.25;
  }
`;

const KpiValue = styled.div<{ $tone?: 'green' | 'strong' }>`
  margin-top: 3px;
  font-size: 17px;
  font-weight: 800;
  line-height: 1.1;
  font-variant-numeric: tabular-nums;
  letter-spacing: 0.2px;
  color: ${(p) =>
    p.$tone === 'green'
      ? (p.theme.colorScheme === 'dark' ? '#4acd8b' : '#1a9f7d')
      : p.theme.text};
`;

/* ── tier-lever bar ── */
const Levers = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  padding: 10px 14px;
  border-radius: 12px;
  margin-bottom: 12px;
  background: ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(255,255,255,0.03)' : 'rgba(22,25,23,0.03)')};
  border: 1px solid ${(p) => p.theme.line};

  /* [PHONE-LAYOUT 2026-10-23, user] Phones: one tidy block instead of a ragged wrap — the title, then the two boxes
     side by side with the label above each (PhoneLeverField, rendered on phones only), the rule line, then Save the
     full width. Without edit rights: the title and the two rule lines, each a full row. */
  @media (max-width: 768px) {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 10px;
    padding: 12px;

    & > * {
      grid-column: 1 / -1;
    }
    & > label {
      grid-column: auto;
    }
  }
`;

/* [PHONE-LAYOUT 2026-10-23, user] Phones only (the isMobile branch of the Tier Bars): a box with its label above it,
   so the two boxes sit side by side in the Levers grid. The SAME inputs as the laptop's (built once in the page). */
const PhoneLeverField = styled.label`
  display: flex;
  flex-direction: column;
  gap: 5px;
  min-width: 0;
  font-size: 12px;
  font-weight: 600;
  color: ${(p) => p.theme.muted};

  & > input {
    width: 100%;
    height: 38px;
    font-size: 14px;
  }
`;

const LeverTitle = styled.span`
  font-size: 10.5px;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: ${(p) => p.theme.muted};
  /* [PHONE-LAYOUT 2026-10-22] */
  @media (max-width: 768px) { font-size: 11px; }
`;

const LeverField = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 7px;
  font-size: 12.5px;
  font-weight: 600;
  color: ${(p) => p.theme.text};
`;

const LeverInput = styled.input`
  width: 84px;
  padding: 5px 8px;
  border-radius: 8px;
  font-size: 12.5px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  color: ${(p) => p.theme.text};
  background: ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(255,255,255,0.06)' : '#fff')};
  border: 1px solid ${(p) => p.theme.line};
  &:focus { outline: none; border-color: ${(p) => p.theme.green600}; }
  /* [LIQUID-GLASS 2026-11-02, user] */
  ${lgSunken}
`;

const LeverStatic = styled.span`
  font-size: 12.5px;
  font-weight: 800;
  color: ${(p) => (p.theme.colorScheme === 'dark' ? '#4acd8b' : p.theme.green600)};
  font-variant-numeric: tabular-nums;
`;

const LeverRule = styled.span`
  font-size: 11.5px;
  color: ${(p) => p.theme.muted};
  font-weight: 500;
`;

// [BUTTON-STYLE 2026-09-30] The green primary from styles/buttons; margin stays (layout).
const SaveBtn = styled.button.attrs<LgPressProps>(LG_PRESS)`
  ${lgButtonPrimary}
  height: 32px; padding: 0 14px; /* [LIQUID-GLASS 2026-11-02, user] the glass capsule at the old size */
  margin-left: auto;

  /* [PHONE-LAYOUT 2026-10-23] Phones: the full width of the Tier Bars block, under the rule line. */
  @media (max-width: 768px) {
    margin-left: 0;
    width: 100%;
    min-height: 38px;
    justify-content: center;
  }
`;

/* ── ranked table ── */
const ScrollContainer = styled.div`
  flex: 1;
  min-height: 0;
  overflow-x: auto;
  overflow-y: scroll;
  padding: 0 16px 56px;   /* dock clearance for the last row */

  /* [PHONE-LAYOUT 2026-10-23, user] Phones: the title, the switch, Roster Total and Tier Bars stayed frozen (about
     430 px of a 390 x 844 screen) and the board scrolled in the ~250 px left. Now this box is as tall as the board
     (no scroll of its own up / down): the board only swipes sideways here and the page scrolls (Container). The
     column header scrolls away with the page, as on Payments. */
  @media (max-width: 768px) {
    flex: none;
    min-height: 0;
    overflow-y: hidden;
    overscroll-behavior-y: auto;
    overscroll-behavior-x: contain;
    -webkit-overflow-scrolling: touch;
  }
`;

/* [VIEW-SLIDE 2026-10-08, user] Range <-> Monthly Scoreboard SLIDES like the app's other view toggles
   (components/ViewSlide): going right (-> Monthly) the Roster Total card and the board leave to the LEFT while the
   monthly ones come in from the RIGHT; back reverses it. Two viewports move in step — the card in the frozen band and
   the board in the scroller (they live in different scroll areas) — while the title, the pill and the Tier Bars stay
   put. The board's viewport clips, so it takes the width of what it holds (the table keeps its 940px minimum) — or a
   narrow screen would lose the table's sideways scroll. */
const PERIODS: readonly ScoreboardPeriod[] = ['last6', 'current'];
const BoardViewport = styled(SlideViewport)`
  width: max-content;
  min-width: 100%;
`;
/** [VIEW-SLIDE 2026-10-08] What one side of the pill shows: the live state, or the frozen copy a leaving pane keeps. */
type BoardView = {
  period: ScoreboardPeriod; month: string; data: SalesScoreboardData | null;
  loading: boolean; saving: boolean; error: string | null; forbidden: boolean;
};

const Table = styled.table`
  width: 100%;
  min-width: 940px;
  border-collapse: collapse;
  font-size: 13px;
  font-variant-numeric: tabular-nums;

  th, td { padding: 10px 12px; white-space: nowrap; text-align: center; }
  th:nth-child(2), td:nth-child(2),
  th:nth-child(3), td:nth-child(3) { text-align: left; }

  thead th {
    position: sticky;
    top: 0;
    z-index: 1;
    background: ${(p) => p.theme.pill};
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: ${(p) => (p.theme.colorScheme === 'dark' ? '#4acd8b' : p.theme.green600)};
    border-bottom: 1px solid ${(p) => p.theme.line};
  }

  tbody td { color: ${(p) => p.theme.text}; }
  tbody tr + tr td { border-top: 1px solid ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)')}; }
`;

// Elite rows get a subtle green tint (mirrors the source sheet's green banding);
// ANY row lifts to a stronger tint on hover so you can see which row the cursor is on.
const Row = styled.tr<{ $elite?: boolean }>`
  background: ${(p) => (p.$elite
    ? (p.theme.colorScheme === 'dark' ? 'rgba(74,205,139,0.10)' : 'rgba(26,159,125,0.16)')
    : 'transparent')};
  transition: background 120ms cubic-bezier(0.4, 0, 0.2, 1);
  &:hover {
    background: ${(p) => (p.$elite
      ? (p.theme.colorScheme === 'dark' ? 'rgba(74,205,139,0.16)' : 'rgba(26,159,125,0.24)')
      : (p.theme.colorScheme === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(22,25,23,0.06)'))};
  }
`;

const Rank = styled.td`
  font-weight: 800;
  color: ${(p) => p.theme.muted};
`;

const Seller = styled.td`
  font-weight: 700;
`;

const Role = styled.td`
  color: ${(p) => p.theme.muted} !important;
  font-weight: 600;
  font-size: 12px;
`;

const TierBadge = styled.span<{ $tier: ScoreboardTier }>`
  display: inline-block;
  padding: 3px 10px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.2px;
  color: ${(p) => {
    const dark = p.theme.colorScheme === 'dark';
    if (p.$tier === 'Elite Class') return dark ? '#4acd8b' : '#1e7c63';
    if (p.$tier === 'Tier B') return dark ? '#fbbf24' : '#b45309';
    return p.theme.muted;
  }};
  background: ${(p) => {
    const dark = p.theme.colorScheme === 'dark';
    if (p.$tier === 'Elite Class') return dark ? 'rgba(74,205,139,0.14)' : 'rgba(26,159,125,0.12)';
    if (p.$tier === 'Tier B') return dark ? 'rgba(251,191,36,0.14)' : 'rgba(180,83,9,0.12)';
    return dark ? 'rgba(255,255,255,0.06)' : 'rgba(22,25,23,0.06)';
  }};
`;

const EmptyState = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  color: ${(p) => p.theme.muted};
  font-size: 14px;
`;

/* [ARRIVAL-30 2026-08-31, user] The footnote under the board explaining how Sales are counted.
   Deliberately quiet — a note, not a warning: it should be there when someone wonders why a
   rep's Sales look lower than their deal list, and invisible the rest of the time. */
// [NET-IN-BANK 2026-09-16] A div, not a p: it now holds one block per note (a p cannot hold divs).
const RuleNote = styled.div`
  /* [2026-08-31, user] "the line should come above the tab pill perfectly". The scroller ends
     56px above its floor, but the floating dock covers the bottom ~82px — so at 4px the note
     sat right under the pill. 40px here puts its baseline ~96px up: clear of the dock, with a
     gap that reads as deliberate rather than as a near-miss. */
  margin: 14px 2px 40px;
  /* [2026-08-31] NO max-width — it was capped at 900px and wrapped to three lines under a
     table many times that wide. It runs as ONE straight row across the board, and only wraps
     if the viewport is genuinely too narrow for it. */
  font-size: 12px;
  line-height: 1.6;
  color: ${(p) => p.theme.muted};

  strong {
    color: ${(p) => p.theme.text};
    font-weight: 700;
  }
`;

const COLS = ['Rank', 'Seller', 'Role', 'Leads', 'Sales', 'Net In-Bank $', 'Monthly In-Bank $', 'ATV', 'Avg, Monthly Conv %', 'Tier'];

/* Skeleton reusing the real table's shape (no layout jump when data lands). */
const TableSkeleton: React.FC = () => (
  <Table aria-busy="true">
    <thead><tr>{COLS.map((c) => <th key={c}>{c}</th>)}</tr></thead>
    <tbody>
      {Array.from({ length: 12 }).map((_, r) => (
        <tr key={r}>
          {COLS.map((_, c) => (
            <td key={c}><SkeletonBox $w={c === 1 ? '80%' : c === 2 ? '70%' : '52%'} $h="13px" $align={c === 1 || c === 2 ? 'left' : 'center'} /></td>
          ))}
        </tr>
      ))}
    </tbody>
  </Table>
);

/* ── component ── */
/** [FLUID-6 2026-10-12, user] "this also feels laggy": the pill's knob used to be measured and moved only after the WHOLE
 *  page had re-rendered for the new side (the 26-row board, the Roster Total, the tier bars), so it lagged the click. The
 *  pill is its own small component now: it remembers the side just picked, so the knob moves at the click, and the page's
 *  own switch follows as a low-priority update (a transition) that can never hold the knob back. A second click while the
 *  page is still catching up is ignored, so the pill and the page can never disagree. */
const PeriodPill: React.FC<{ period: ScoreboardPeriod; last6Label: string; saving: boolean; onPick: (p: ScoreboardPeriod) => void }> = ({ period, last6Label, saving, onPick }) => {
  const [picked, setPicked] = useState<ScoreboardPeriod | null>(null);
  const [pending, startPick] = useTransition();
  // The page has caught up (or moved on, or did not take the pick): hand back to it. [FLUID-8 2026-10-12] `pending`
  // too, so a pick the page refused can never leave the knob on the wrong side.
  useEffect(() => { if (!pending) setPicked(null); }, [pending, period]);
  const shown = picked ?? period;
  // Knob geometry — measured from the active button because the two labels differ in width.
  const optRefs = useRef<Partial<Record<ScoreboardPeriod, HTMLButtonElement | null>>>({});
  const [knob, setKnob] = useState({ left: 0, width: 0 });
  useLayoutEffect(() => {
    const el = optRefs.current[shown];
    if (!el) return;
    setKnob({ left: el.offsetLeft, width: el.offsetWidth });
    // Re-measure when a label's text changes (month rollover) as well as on switch.
  }, [shown, last6Label]);
  const pick = (p: ScoreboardPeriod) => {
    if (p === shown || pending) return;
    setPicked(p);
    startPick(() => onPick(p));
  };
  // [FLUID-8 2026-10-12, user] …on the mouse PRESS (the click only comes on release); touch / keyboard click.
  const pressedRef = useRef(false);
  const press = (p: ScoreboardPeriod) => ({
    onPointerDown: (e: React.PointerEvent) => {
      pressedRef.current = isPlainMousePress(e);
      if (pressedRef.current) pick(p);
    },
    onClick: (e: React.MouseEvent) => {
      if (clickEndsPress(pressedRef, e)) return; // the press already switched
      pick(p);
    },
  });
  return (
    <Pill role="tablist" aria-label="Scoreboard period">
      <PillKnob $left={knob.left} $width={knob.width} />
      <PillOpt
        ref={(el) => { optRefs.current.last6 = el; }}
        type="button"
        role="tab"
        aria-selected={shown === 'last6'}
        $active={shown === 'last6'}
        {...press('last6')} /* [VIEW-SLIDE 2026-10-08] */
        disabled={saving}
      >
        {last6Label}
      </PillOpt>
      {/* [MONTH-PICKER 2026-09-02, user] Fixed words, not the month name: this side is
          "one month", and the dropdown below names which. */}
      <PillOpt
        ref={(el) => { optRefs.current.current = el; }}
        type="button"
        role="tab"
        aria-selected={shown === 'current'}
        $active={shown === 'current'}
        {...press('current')} /* [VIEW-SLIDE 2026-10-08] */
        disabled={saving}
      >
        Monthly Scoreboard
      </PillOpt>
    </Pill>
  );
};

const SalesScoreboardPage: React.FC = () => {
  const { user } = useAuth();
  const isMobile = useIsMobile(); // [SCOREBOARD PHONE KPI 2026-09-23, user] see the Roster Total tiles below
  // Tier bars are editable by seniors + admins (mirrors the server gate); everyone
  // else sees them read-only.
  const canEditLevers = !!user?.is_admin || !!user?.is_senior;

  const [data, setData] = useState<SalesScoreboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [forbidden, setForbidden] = useState(false); // [LOAD-STATES 2026-09-29] a 403 is final, no countdown
  const [saving, setSaving] = useState(false);

  // Lever draft (senior/admin edit) — seeded from the loaded data, editable before save.
  const [inbankDraft, setInbankDraft] = useState('');
  const [convDraft, setConvDraft] = useState('');

  // [PERIOD-PILL 2026-08-14] Which window the board shows. Defaults to the six-month board, so
  // the page opens exactly as it did before this feature existed.
  const [period, setPeriod] = useState<ScoreboardPeriod>('last6');

  // [MONTH-PICKER 2026-09-02, user] Resolved ONCE — effectiveCurrentMonth() builds an
  // Intl.DateTimeFormat per call, and this is read on every render below.
  const currentYm = useMemo(() => effectiveCurrentMonth(), []);

  /**
   * [MONTH-PICKER 2026-09-02, user] WHICH month the monthly side shows. Starts at the effective
   * current month, so the page (and the pill's right side) opens on exactly the board it did
   * before the dropdown existed — the picker adds reach backwards, it changes no default.
   */
  const [month, setMonth] = useState<string>(currentYm);

  /** Every month the dropdown offers — the current one down to the app-wide Jan-2026 floor,
   *  newest first. Bounded iteration so a nonsense clock can never spin here. */
  const monthOptions = useMemo(() => {
    const out: string[] = [];
    for (let m = currentYm, i = 0; m >= MIN_DROPDOWN_MONTH && i < 240; m = addMonthsToYYYYMM(m, -1), i += 1) {
      out.push(m);
    }
    return out.length ? out : [currentYm];
  }, [currentYm]);

  /**
   * Pill labels are derived CLIENT-side from the shared effective-current-month util, NOT from
   * the loaded payload. That matters: `data.months` only ever describes the period currently
   * loaded, so reading the left label from it would leave it blank (or wrong) while the right
   * side is showing. Both labels therefore stay correct and stable whichever side is active.
   * The 6 mirrors MONTHS_IN_PERIOD in services/salesScoreboard.ts.
   *
   * [MONTH-PICKER 2026-09-02, user] The RIGHT label is now the fixed words "Monthly Scoreboard"
   * — the dropdown beside the roster meta says which month, so naming the month twice in the
   * header would only invite the two to disagree while a load is in flight.
   */
  const last6Label = useMemo(() => {
    const lastCompleted = addMonthsToYYYYMM(currentYm, -1);   // the window ends at last month
    const firstOfSix = addMonthsToYYYYMM(currentYm, -6);
    // Range only, full years — the "Last 6 completed months ·" prefix was dropped at the
    // user's request once the pill made the two windows self-evident.
    return `${fmtMonthYYYY(firstOfSix)} – ${fmtMonthYYYY(lastCompleted)}`;
  }, [currentYm]);

  // [FLUID-6 2026-10-12] The pill's knob is measured inside PeriodPill (above) now.

  /**
   * [MONTH-PICKER 2026-09-02] The month actually SENT. Undefined when it is the effective
   * current month: that request — 'current' with no month — is the one the idle prefetch warms
   * and the server reads identically, so the default monthly view still opens from cache. It is
   * also undefined on the six-month side, which ignores the month entirely; sending it there
   * would only fork that side's cache entry away from the prefetched one.
   */
  const monthParam = period === 'current' && month !== currentYm ? month : undefined;

  // [VIEW-SLIDE 2026-10-08] Tags each load so a slower earlier answer (a quick flip there and back) can never land on
  // top of a newer one and put one side's board under the other side of the pill.
  const reqIdRef = useRef(0);
  // [VIEW-SLIDE 2026-10-08] Set by pickPeriod when the arriving side was put on screen from the app's GET cache.
  const seededRef = useRef(false);

  // [VIEW-SLIDE 2026-10-08] `seeded` = that side's board is already on screen (pickPeriod), so no skeleton; the
  // request itself is the same one as before.
  const load = useCallback(async (p: ScoreboardPeriod, m?: string, seeded = false) => {
    const reqId = ++reqIdRef.current;
    if (!seeded) setLoading(true);
    setError(null);
    try {
      const d = await fetchSalesScoreboard(p, m);
      if (reqId !== reqIdRef.current) return;
      setData(d);
      // Reseed the lever drafts from THIS period's bars — the two periods hold independent
      // thresholds, so switching must not leave the other side's numbers in the inputs.
      setInbankDraft(String(Math.round(d.levers.eliteMonthlyInBank)));
      setConvDraft(String(d.levers.eliteConversion));
    } catch (e: any) {
      if (reqId !== reqIdRef.current) return;
      setError(e?.response?.data?.error || 'Failed to load the Sales Scoreboard.');
      setForbidden(isForbidden(e)); // [LOAD-STATES 2026-09-29]
    } finally {
      if (reqId === reqIdRef.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    const seeded = seededRef.current; // [VIEW-SLIDE 2026-10-08]
    seededRef.current = false;
    load(period, monthParam, seeded);
  }, [load, period, monthParam]);

  // [VIEW-SLIDE 2026-10-08, user] The side sliding OUT keeps what it had on screen (frozen at the switch).
  const [snap, setSnap] = useState<BoardView | null>(null);
  const live: BoardView = { period, month, data, loading, saving, error, forbidden };

  /* [VIEW-SLIDE 2026-10-08, user] The pill. No blink: when the app's GET cache already holds the arriving side, it is
     on screen in the same frame (board, Roster Total, Tier Bars) — no skeleton; otherwise the skeleton shows from the
     first frame, never a frame of the other side's rows. Either way the effect above then runs the same request as
     before. */
  const pickPeriod = (p: ScoreboardPeriod) => {
    if (p === period) return;
    setSnap(live);
    const seed = peekCache<SalesScoreboardData>('/sales-scoreboard', { period: p, month: p === 'current' && month !== currentYm ? month : undefined });
    seededRef.current = !!seed;
    if (seed) {
      setData(seed);
      setInbankDraft(String(Math.round(seed.levers.eliteMonthlyInBank)));
      setConvDraft(String(seed.levers.eliteConversion));
      setError(null);
      setLoading(false);
    } else {
      setLoading(true);
    }
    setPeriod(p);
  };

  /* The dropdown only exists on the monthly side (below), so this just moves the month — the
     period is already 'current' whenever it can be called. The chosen month PERSISTS across a
     trip to the six-month board and back, so the pill returns you to the month you were on
     rather than silently resetting to today's. */
  const pickMonth = useCallback((m: string) => setMonth(m), []);

  const rows = data?.rows ?? [];
  const levers = data?.levers;

  // [FLUID-6 2026-10-12, user] "doesn't have the animation when someone comes to that tab". When the board's rows land
  // after their skeleton (a tab visit, a month pick, a side of the pill that was not cached, a tier-bar save) every
  // row on screen rises in, top to bottom, as one wave (components/Fluid.tsx) — on the side on show only, never the
  // one sliding out. A switch whose side came from the cache keeps the key, so only the slide's own wave runs then,
  // and the quiet re-check behind it moves nothing.
  const boardLandRef = useRef<HTMLDivElement>(null);
  useRowsLand(boardLandRef, !loading && !saving && !error && rows.length > 0 ? 'land' : null);
  // …and the Roster Total tiles rise in when their figures replace the skeleton bars (not on a save: they keep theirs).
  const tilesLand = useLandWindow(!loading && !error && !!data, 'tiles');

  const leverDirty = !!levers && (
    Number(inbankDraft) !== Math.round(levers.eliteMonthlyInBank) ||
    Number(convDraft) !== levers.eliteConversion
  );

  // [MOTION-B 2026-08-20] This button stays on screen after the save, so it carries the
  // confirmation itself: spinner while saving, then a drawn tick held briefly. `saving`
  // still drives the disabled state and the table skeleton exactly as before.
  const savePhase = useSavePhase();

  const saveLevers = async () => {
    const inbank = Number(inbankDraft);
    const conv = Number(convDraft);
    if (!Number.isFinite(inbank) || !Number.isFinite(conv)) { toastError('Enter valid lever values.'); return; }
    setSaving(true);
    try {
      await savePhase.run(async () => {
        // Period travels with the write — the two sides keep independent bars, so saving
        // while "current month" is showing must not move the six-month board (or vice versa).
        // [MONTH-PICKER 2026-09-02] The month travels too: it changes nothing about what is
        // saved (all months share the monthly bars) but the response REPLACES what is on
        // screen, so saving from August has to come back as August.
        const d = await updateScoreboardLevers(
          { eliteMonthlyInBank: inbank, eliteConversion: conv }, period, monthParam);
        setData(d);
        setInbankDraft(String(Math.round(d.levers.eliteMonthlyInBank)));
        setConvDraft(String(d.levers.eliteConversion));
      });
      toastSuccess('Tier bars updated');
    } catch (e: any) {
      toastError(e?.response?.data?.error || 'Failed to update tier bars.');
    } finally {
      setSaving(false);
    }
  };

  // [VIEW-SLIDE 2026-10-08, user] The Roster Total card for one side of the pill (live, or a leaving pane's frozen
  // copy) — exactly as rendered before. The tiles carry data-slide-item so they cascade in on a switch.
  const rosterCard = (v: BoardView) => {
    const roster = v.data?.roster;
    const win = v.data?.months ?? [];
    // [PERIOD-PILL 2026-08-14] A one-month window collapses to a single label — the range form
    // rendered "Aug-2026 – Aug-2026", naming the same month twice. Full years throughout, matching
    // the pill, so the two dates on screen can't read as different formats.
    const windowLabel = win.length === 0
      ? ''
      : win.length === 1
        ? fmtMonthYYYY(win[0])
        : `${fmtMonthYYYY(win[0])} – ${fmtMonthYYYY(win[win.length - 1])}`;
    const kpiValue = (n: number | undefined, fmt: (n: number) => string) =>
      v.loading || n === undefined ? <SkeletonBox $w="90px" $h="17px" $radius="6px" /> : fmt(n);
    const kpiTiles = (
      <KpiRow $land={tilesLand && v.period === period /* [FLUID-6 2026-10-12] the side on show only */}>
        <Kpi data-slide-item><KpiLabel>Reps</KpiLabel><KpiValue $tone="strong">{kpiValue(roster?.reps, fmtInt)}</KpiValue></Kpi>
        <Kpi data-slide-item><KpiLabel>Leads</KpiLabel><KpiValue>{kpiValue(roster?.leads, fmtInt)}</KpiValue></Kpi>
        <Kpi data-slide-item><KpiLabel>Sales</KpiLabel><KpiValue>{kpiValue(roster?.sales, fmtInt)}</KpiValue></Kpi>
        <Kpi data-slide-item><KpiLabel>Net In-Bank $</KpiLabel><KpiValue $tone="green">{kpiValue(roster?.inBank, fmtCurrency0)}</KpiValue></Kpi>
        <Kpi data-slide-item><KpiLabel>Monthly In-Bank $</KpiLabel><KpiValue $tone="green">{kpiValue(roster?.monthlyInBank, fmtCurrency0)}</KpiValue></Kpi>
        <Kpi data-slide-item><KpiLabel>ATV</KpiLabel><KpiValue>{kpiValue(roster?.atv, fmtCurrency0)}</KpiValue></Kpi>
        <Kpi data-slide-item><KpiLabel>Avg, Monthly Conv %</KpiLabel><KpiValue>{kpiValue(roster?.avgMoConv, fmtPct)}</KpiValue></Kpi>
      </KpiRow>
    );
    return (
      <Snapshot>
        <SnapHead>
          <SnapTitle>Roster Total</SnapTitle>
          {/* [MONTH-PICKER 2026-09-02, user] The meta keeps reporting the window ACTUALLY
              loaded (so it lags a beat behind the dropdown while a month loads, and is
              therefore always true of the numbers below it); the select carries the choice.
              MONTHLY SIDE ONLY, at the user's request — the six-month board spans six months,
              so a month filter beside it would be a control with nothing to control. The pill
              is the way in and out. */}
          <SnapRight>
            <SnapMeta>{roster ? `${roster.reps} reps` : ''}{windowLabel ? ` · ${windowLabel}` : ''}</SnapMeta>
            {v.period === 'current' && (
              <MonthSelect liquid
                value={v.month}
                deferChange /* [FLUID-8 2026-10-12] instant pick */ onChange={(e) => pickMonth(e.target.value)}
                disabled={saving}
                title="Show one month's scoreboard"
              >
                {monthOptions.map((m) => (
                  <option key={m} value={m}>{fmtMonthFull(m)}</option>
                ))}
              </MonthSelect>
            )}
          </SnapRight>
        </SnapHead>
        {/* [SCOREBOARD PHONE KPI 2026-09-23, user] "the roster total is showing too small". On phones
            ScaleToFit pinned the row to its 1,110px desktop width and shrank it to its 50% floor, on
            top of the KpiRow/Kpi phone carousel (2 tiles + a peek), so the carousel's tiles came out
            half-size (labels ~4.75px, values ~8.5px). Phones now get the carousel alone, at full
            size; the user picked the swipe row over a 2-column grid. Desktop keeps ScaleToFit. */}
        {isMobile ? kpiTiles : <ScaleToFit contentMinWidth={kpiRowMinWidth(7)}>{kpiTiles}</ScaleToFit>}
      </Snapshot>
    );
  };

  // [VIEW-SLIDE 2026-10-08, user] The ranked board for one side of the pill — exactly as rendered before. `isLive` =
  // the side on show; a leaving pane never mounts the retry card (its countdown would fetch for a pane nobody sees).
  const board = (v: BoardView, isLive: boolean) => {
    const vRows = v.data?.rows ?? [];
    return v.loading || v.saving ? (
      <TableSkeleton />
    ) : v.error ? (
      // [LOAD-STATES 2026-09-29, user] Was the raw server text ("Internal server error").
      v.forbidden ? <EmptyState>{v.error}</EmptyState> : isLive ? <LoadFailed what="the Sales Scoreboard" onRetry={() => load(period, monthParam)} /> : null
    ) : vRows.length === 0 ? (
      <EmptyState>
        {/* [MONTH-PICKER 2026-09-02] "yet" belongs only to the month still running — a
            settled month with nothing in it is not waiting for anything. */}
        {v.period === 'current'
          ? `No sales activity ${v.month === currentYm ? 'yet ' : ''}in ${fmtMonthFull(v.month)}.`
          : `No sales activity in the last ${v.data?.levers?.monthsInPeriod ?? 6} months.`}
      </EmptyState>
    ) : (
      <Table>
        <thead>
          <tr>
            <th>Rank</th><th>Seller</th><th>Role</th><th>Leads</th><th>Sales</th>
            <th>Net In-Bank $</th><th>Monthly In-Bank $</th><th>ATV</th><th>Avg, Monthly Conv %</th><th>Tier</th>
          </tr>
        </thead>
        <tbody>
          {vRows.map((r: ScoreboardRow) => (
            <Row key={r.seller} $elite={r.tier === 'Elite Class'}>
              <Rank>{r.rank}</Rank>
              <Seller>{r.seller}</Seller>
              <Role>{r.role}</Role>
              <td>{fmtInt(r.leads)}</td>
              <td>{fmtInt(r.sales)}</td>
              <td>{fmtCurrency(r.inBank)}</td>
              <td>{fmtCurrency(r.monthlyInBank)}</td>
              <td>{fmtCurrency(r.atv)}</td>
              <td>{fmtPct(r.avgMoConv)}</td>
              <td><TierBadge $tier={r.tier}>{r.tier}</TierBadge></td>
            </Row>
          ))}
        </tbody>
      </Table>
    );
  };

  // [PHONE-LAYOUT 2026-10-23] The two Tier Bar inputs, built ONCE: the laptop keeps them inline in their LeverField
  // exactly as before; phones put each under its label (PhoneLeverField). Same state, same handlers.
  const inbankInput = (
    <LeverInput type="number" min={0} step={100} value={inbankDraft}
      onChange={(e) => setInbankDraft(e.target.value)} disabled={loading || saving} />
  );
  const convInput = (
    <LeverInput type="number" min={0} max={100} step={0.1} value={convDraft}
      onChange={(e) => setConvDraft(e.target.value)} disabled={loading || saving} />
  );

  return (
    <Container>
      <FrozenTop>
        <HeaderRow>
          {/* [SCOREBOARD-US 2026-09-17, user] Heading reads "Sales Scoreboard (US)". */}
          <PageTitle>Sales Scoreboard (US)</PageTitle>
          {/* [PERIOD-PILL 2026-08-14] Replaces the static window caption. Layout below is
              identical for both sides — only the data behind it changes. */}
          <PeriodPill period={period} last6Label={last6Label} saving={saving} onPick={pickPeriod} />
        </HeaderRow>

        {/* [VIEW-SLIDE 2026-10-08, user] The Roster Total card slides, in step with the board below. */}
        <SlideViewport>
          {PERIODS.map((p) => (
            <SlidePane key={p} $pos={slidePos(period, p, PERIODS)} $quiet /* [FLUID-6 2026-10-12] a switch only slides */ aria-hidden={period !== p} data-scoreboard-roster={p}>
              {p === period ? rosterCard(live) : snap?.period === p ? rosterCard(snap) : null}
            </SlidePane>
          ))}
        </SlideViewport>

        {/* Tier bars — Elite clears BOTH, Tier B clears EITHER, Tier C clears NEITHER.
            Admin edits the two thresholds and every tier recomputes on save. */}
        <Levers>
          <LeverTitle>Tier Bars</LeverTitle>
          {canEditLevers ? (
            isMobile ? (
              // [PHONE-LAYOUT 2026-10-23, user] Phones: the two boxes side by side, the label above each.
              <>
                <PhoneLeverField><span>Monthly In-Bank ≥ ($)</span>{inbankInput}</PhoneLeverField>
                <PhoneLeverField><span>Conversion ≥ (%)</span>{convInput}</PhoneLeverField>
              </>
            ) : (
            <>
              <LeverField>Monthly In-Bank ≥ $
                {inbankInput}
              </LeverField>
              <LeverField>Conversion ≥
                {convInput}%
              </LeverField>
            </>
            )
          ) : (
            <LeverRule>
              Monthly In-Bank ≥ <LeverStatic>{levers ? fmtCurrency0(levers.eliteMonthlyInBank) : '—'}</LeverStatic>
              {'  ·  '}Conversion ≥ <LeverStatic>{levers ? fmtPct(levers.eliteConversion) : '—'}</LeverStatic>
            </LeverRule>
          )}
          <LeverRule>Elite = clears both · Tier B = clears either · Tier C = clears neither</LeverRule>
          {canEditLevers && (
            <SaveBtn onClick={saveLevers} disabled={!leverDirty || saving || loading}>
              <SaveButtonContent phase={savePhase.phase} label="Save" doneLabel="Saved" />
            </SaveBtn>
          )}
        </Levers>
      </FrozenTop>

      <ScrollContainer>
        {/* [VIEW-SLIDE 2026-10-08, user] Both sides' boards stay mounted and slide; the range rests on the LEFT, the
            Monthly Scoreboard on the RIGHT (their sides of the pill). The footnote below never moves. */}
        <BoardViewport>
          {PERIODS.map((p) => (
            <SlidePane key={p} $pos={slidePos(period, p, PERIODS)} $quiet aria-hidden={period !== p} data-scoreboard-pane={p}
              ref={p === period ? boardLandRef : undefined /* [FLUID-6 2026-10-12] the rows' wave: the side on show */}>
              {p === period ? board(live, true) : snap?.period === p ? board(snap, false) : null}
            </SlidePane>
          ))}
        </BoardViewport>
        {/* [ARRIVAL-30 2026-08-31, user] The counting rule, in plain English, at the very end of
            the tab — "it will be easy for all to understand the data there". It sits INSIDE the
            scroll container so it reads as the table's footnote, and it is hidden while the
            board is empty or erroring, where it would be explaining nothing. */}
        {!loading && !error && rows.length > 0 && (
          <RuleNote>
            <div>
              <strong>How Sales are counted:</strong> a website counts as one sale in the month its
              first deal closes. Another deal on the same website by the same rep within 30 days
              adds its <strong>amount but not a second sale</strong>; after 30 days it counts
              again. Two reps closing the same website each keep their own sale.
            </div>
            {/* [NET-IN-BANK 2026-09-16, user] The basis of the Net In-Bank $ column, one line. */}
            <div>
              <strong>How Net In-Bank is calculated:</strong> the rep&apos;s sales for the month, minus
              the refunds and disputes under their name for the Sales team on the Refunds / Disputes
              page, plus any disputes won.
            </div>
          </RuleNote>
        )}
      </ScrollContainer>
    </Container>
  );
};

export default SalesScoreboardPage;
